import { Field, FieldType } from '../../types';
import { OccupancyType } from './data';
import { flatMap } from 'lodash';

export type SectionType = {
  [key: string]: any;
};

export enum Root {
  Property = 'property',
  Driver = 'driver',
  Submission = 'submission',
  Client = 'client',
  AgentQuestions = 'agentQuestions',
}

export interface QuestionCondition<
  T extends Record<string, any> = Record<string, unknown>,
> {
  name: keyof T;
  op: 'Equals';
  value: any;
}

export type Question<T extends Record<string, any> = Record<string, unknown>> =
  Field & {
    title: string;
    subtitle?: string;
    /** The property on the object to update on the model */
    name: keyof T;
    conditions?: QuestionCondition<T>[];
    sectionName?: string;
  };

export interface RootSection {
  sectionName: string;
  questions: Question[];
}

export interface RootType {
  [key: string]: RootSection[];
}

export const ROOTS: RootType = {
  property: [
    {
      sectionName: 'misc',
      questions: [
        {
          title: 'Do you own this property?',
          subtitle: '',
          type: FieldType.Occupancy,
          label: '',
          name: 'occupancyType',
        },
        {
          title: 'How many years have you been at this residence?',
          subtitle: '',
          type: FieldType.Number,
          options: {
            max: 100,
          },
          label: '',
          name: 'yearsAtResidence',
        },
      ],
    },
    {
      sectionName: 'pool',
      questions: [
        {
          title: 'Do you have a pool?',
          type: FieldType.BinarySelect,
          values: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          label: '',
          name: 'swimmingPool',
        },
        {
          title: 'Does your pool have a fence?',
          type: FieldType.BinarySelect,
          values: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          conditions: [
            {
              op: 'Equals',
              name: 'pool.swimmingPool',
              value: true,
            },
          ],
          label: '',
          name: 'poolFence',
        },
        {
          title: 'What type of pool do you have?',
          type: FieldType.Pool,
          conditions: [
            {
              op: 'Equals',
              name: 'pool.swimmingPool',
              value: true,
            },
          ],
          label: '',
          name: 'poolType',
        },
      ],
    },
    {
      sectionName: 'animals',
      questions: [
        {
          title: 'Do you have any dogs?',
          type: FieldType.BinarySelect,
          values: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          label: '',
          name: 'haveDogs',
        },
        {
          title: 'What breed of dogs do you have?',
          type: FieldType.Dog,
          conditions: [
            {
              op: 'Equals',
              name: 'animals.haveDogs',
              value: true,
            },
          ],
          label: '',
          name: 'dogs',
        },
      ],
    },
    {
      sectionName: 'roof',
      questions: [
        {
          title: 'What is the approximate age of your roof?',
          subtitle: 'The age of your roof is required for an accurate quote.',
          type: FieldType.Number,
          options: {
            max: 100,
          },
          conditions: [
            {
              op: 'Equals',
              name: 'occupancyType',
              value: OccupancyType.Owner,
            },
          ],
          label: 'Years old',
          name: 'roofAge',
        },
        {
          title: 'What is the material of your roof?',
          type: FieldType.Roof,
          conditions: [
            {
              op: 'Equals',
              name: 'occupancyType',
              value: OccupancyType.Owner,
            },
          ],
          label: '',
          name: 'roofMaterial',
        },
      ],
    },
  ],
};

export function getFlatMapOfRootQuestions(root: Root) {
  return flatMap(ROOTS[root], (r) =>
    r.questions.map((q) => {
      return { ...q, sectionName: r.sectionName };
    }),
  );
}
